import { useState } from "react";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { MockGenieConfig } from "@/types/mockups/genie-setup-v2";

import { personalityPresets } from "../data";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface TypeSetupProps {
  onBack: () => void;
  onComplete: (config: MockGenieConfig) => void;
}

const promptExamples = [
  "A friendly support genie for our SaaS dashboard that answers questions about features and troubleshoots common issues.",
  "A lead capture genie for our website — qualifies visitors, books demo calls, and saves their info.",
  "A survey genie that asks customers 3 short questions after they buy and saves their feedback.",
  "An onboarding genie that walks new users through the first 5 things they need to set up.",
];

const inferConfig = (description: string): MockGenieConfig => {
  const lower = description.toLowerCase();
  let categoryId: string | null = null;
  if (lower.includes("support") || lower.includes("help") || lower.includes("troubleshoot"))
    categoryId = "provide-support";
  else if (lower.includes("lead") || lower.includes("demo") || lower.includes("book"))
    categoryId = "capture-leads";
  else if (lower.includes("survey") || lower.includes("feedback") || lower.includes("insight"))
    categoryId = "gather-insights";
  else if (lower.includes("onboard") || lower.includes("walkthrough") || lower.includes("learn"))
    categoryId = "onboard-educate";
  else if (lower.includes("market") || lower.includes("sell") || lower.includes("offer"))
    categoryId = "amplify-marketing";

  let preset = personalityPresets.find((p) => p.id === "the-concierge")!;
  if (lower.includes("friendly") || lower.includes("warm") || lower.includes("casual"))
    preset = personalityPresets.find((p) => p.id === "the-friend")!;
  if (lower.includes("coach") || lower.includes("motivate") || lower.includes("excited"))
    preset = personalityPresets.find((p) => p.id === "the-coach")!;
  if (lower.includes("expert") || lower.includes("detailed") || lower.includes("technical"))
    preset = personalityPresets.find((p) => p.id === "the-expert")!;

  return {
    job: { categoryId },
    personality: preset.values,
    playbookIds: [],
    knowledge: { sources: [], customContext: description },
    welcomeMessage: "",
    genieName: "",
  };
};

export const TypeSetup = ({ onBack, onComplete }: TypeSetupProps) => {
  const [text, setText] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);

  const analysisLines = [
    "Reading your description...",
    "Picking the right job...",
    "Choosing a personality...",
    "Suggesting plays to run...",
    "Ready to review.",
  ];

  const handleAnalyze = async () => {
    if (!text.trim()) return;
    setIsAnalyzing(true);
    for (let i = 0; i < analysisLines.length; i++) {
      await new Promise((r) => setTimeout(r, 700));
      setAnalysisStep(i + 1);
    }
    await new Promise((r) => setTimeout(r, 400));
    onComplete(inferConfig(text));
  };

  return (
    <div className="py-6">
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-genie-primary flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to setup mode
        </button>
      </div>

      <div className="mb-6 flex items-start gap-4">
        <GenieIcon name="chat" size={48} className="h-12 w-12 shrink-0" />
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#222B45] leading-tight">
            Describe your genie in your own words
          </h2>
          <p className="text-sm md:text-base text-[#64748B] mt-2">
            Tell us what you need. We'll set everything up for you to review.
          </p>
        </div>
      </div>

      {!isAnalyzing ? (
        <div className="space-y-5">
          <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <div className="rounded-[14px] bg-white p-1">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                placeholder="Example: A friendly genie for my coffee shop website that answers questions about hours, menu, and helps people book the upstairs space for events."
                className="w-full rounded-[10px] px-4 py-3 text-base focus:outline-none resize-none placeholder:text-gray-400"
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Need inspiration?
            </p>
            <div className="space-y-2">
              {promptExamples.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => setText(ex)}
                  className="w-full text-left text-sm px-4 py-3 rounded-lg border border-gray-200 hover:border-genie-primary hover:bg-genie-primary/5 text-gray-700 transition-colors"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleAnalyze}
              disabled={!text.trim()}
              className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 transition-all duration-200 gap-2"
            >
              Set up my genie
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-genie-primary/10 text-genie-primary flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Setting up your genie</h3>
              <p className="text-xs text-gray-500">This takes just a moment.</p>
            </div>
          </div>
          <div className="space-y-2.5">
            {analysisLines.map((line, i) => {
              const isActive = i === analysisStep;
              const isDone = i < analysisStep;
              return (
                <div
                  key={line}
                  className={`flex items-center gap-3 text-sm transition-opacity ${
                    isActive || isDone ? "opacity-100" : "opacity-40"
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                      isDone
                        ? "bg-genie-primary text-white"
                        : isActive
                          ? "bg-genie-primary/20 text-genie-primary animate-pulse"
                          : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    {isDone ? "✓" : i + 1}
                  </div>
                  <span className={isDone || isActive ? "text-gray-900" : "text-gray-500"}>
                    {line}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
